// Device tier from viewport width (APP_SHELL.md). Templates lay out from
// this, never from media queries in page CSS, so the same code works inside
// a drawer or a narrow panel.
//
// A PANE HAS A TIER OF ITS OWN (PLAN-2026-10-17 §2.3). A tool drawn in a
// pane — Players taking the room beside My Team's roster — lays itself out by
// the room it is given, not by the window: Players in a pane 900 px wide on a
// 1280 window draws its columns for 900. `TierScope` says so for everything
// inside it, and stamps the tier on its box, so a stylesheet rule written
// `[data-tier='phone'] …` (a phone's screen scrolling as one, UX-38) reaches
// a pane at a phone's width. A rule written for the window's tier
// (`.fsp-frame[data-tier=…]`) still answers the window, and a bare
// `[data-tier='tablet'] …` matches the frame around a narrower pane too.

import { createContext, createElement, useContext, type ReactNode } from 'react';
import { useViewportWidth } from '../kit/viewport';

export type Tier = 'phone' | 'tablet' | 'desktop' | 'large';

export function tierFor(width: number): Tier {
  if (width <= 600) return 'phone';
  if (width <= 1100) return 'tablet';
  if (width <= 1400) return 'desktop';
  return 'large';
}

const PaneTier = createContext<Tier | null>(null);

export function useTier(): Tier {
  const pane = useContext(PaneTier);
  const tier = useViewportWidth(tierFor);
  return pane ?? tier;
}

/** The window's own tier, whatever pane this is drawn in: the shell's question. */
export function useWindowTier(): Tier {
  return useViewportWidth(tierFor);
}

/**
 * Back to the window's tier, for what a pane opens over the whole window: a
 * sheet drawn from inside the room is the window's size, not the pane's, and
 * React hands the pane's tier through the portal it is drawn in.
 */
export function WindowTier({ children }: { children: ReactNode }) {
  return createElement(PaneTier.Provider, { value: null }, children);
}

/** Everything inside lays out for `tier`, the pane's, rather than the window's. */
export function TierScope({ tier, children }: { tier: Tier; children: ReactNode }) {
  return createElement(
    PaneTier.Provider,
    { value: tier },
    createElement('div', { className: 'fsp-tier-scope', 'data-tier': tier }, children),
  );
}
